/**
 * Site Context — user-declared ground truth about a site.
 *
 * Site_Context_Prompt SC1. A freeform "What we know about this site"
 * prose field captured on site creation and every rerun. Wired into the
 * LLM prompt as the highest-priority context block (before SED, before
 * DAM) with explicit "treat as ground truth, flag any provider-data
 * contradictions" instructions.
 *
 * Lifecycle:
 *   1. User types prose in the SiteContextInput component.
 *   2. extractClaims (Gemini Flash) parses the prose into ExtractedClaim[].
 *   3. crossCheckClaims (deterministic) compares claims against the SED
 *      and emits ClaimConflict[] at HIGH / MEDIUM / LOW severity.
 *   4. UI surfaces conflicts inline — HIGH blocks submit until ack.
 *   5. On report generation, the entire SiteContext is snapshotted onto
 *      `report.siteContextSnapshot` so prior reports stay reproducible.
 *   6. buildSiteContextBlock(siteContext) prepends to every report-type
 *      prompt.
 */

/**
 * Categorisation tag for an extracted claim. The cross-checker has one
 * comparator per category that returns a conflict (or null) against the
 * matching SED field.
 *
 * Add a category here AND a comparator in `comparators.ts` in lockstep.
 */
export type ClaimCategory =
  | 'power.grid'
  | 'power.btm'
  | 'power.timeline'
  | 'water.cooling'
  | 'water.source'
  | 'water.rights'
  | 'environmental.species'
  | 'environmental.wetlands'
  | 'environmental.nepa'
  | 'environmental.tribal'
  | 'environmental.phase1'
  | 'regulatory.zoning'
  | 'regulatory.permits'
  | 'regulatory.air'
  | 'geotechnical.foundation'
  | 'geotechnical.seismic'
  | 'parcel.boundaries'
  | 'parcel.acres'
  | 'connectivity.fiber'
  | 'connectivity.latency'
  | 'schedule.inservice'
  | 'schedule.permitting'
  | 'finance.cost'
  | 'finance.ppa'
  | 'technology.reactor'
  | 'technology.cooling'
  | 'technology.workload'
  | 'prior-study'
  | 'doc-reference'
  | 'commitment'
  | 'unverifiable'
  // DR1 — site-level umbrella category for claims that don't slot
  // into the comparator namespace above. Used by the legacy
  // `site.description` migrator (DR pack 2026-05) to lift the
  // freeform brief into a SiteContext claim without needing a new
  // SED comparator. Future SC authors should prefer the specific
  // categories above; 'general' is the catch-all.
  | 'general';

/**
 * Dot-path into the SED's data — validated at runtime against the
 * `resolveSedField` allow-list in `crossCheckClaims.ts`. Kept as a
 * string alias for ergonomic LLM emission; the runtime guard is in
 * the cross-checker, not the type.
 */
export type SedFieldPath = string;

/**
 * Part C SP4 S5 — passage-offset provenance. A character span into a
 * specific source document so a citation / claim can later deep-link to
 * the exact passage it was derived from (vs the current
 * filename-or-page-only granularity).
 *
 * `start` / `end` are 0-based UTF-16 code-unit offsets into the source
 * document's plain-text content (`SiteDocument.textContent`), half-open
 * `[start, end)`. `docId` (when carried) identifies WHICH document the
 * span indexes into; on types that are already scoped to a single doc
 * (e.g. `LinkedDocumentRef.docId`) it is redundant and omitted.
 *
 * TYPES + PLUMBING ONLY in this slice — no producer populates these yet
 * (population is a follow-on SED step) and no consumer is required to
 * read them. Every field is OPTIONAL so existing persisted data without
 * offsets still typechecks and renders unchanged.
 */
export interface PassageOffset {
  /** 0-based UTF-16 start offset into the source doc's text content. */
  start: number;
  /** 0-based UTF-16 end offset (half-open `[start, end)`). */
  end: number;
}

/**
 * A single typed claim extracted from the freeform site-context prose.
 * Populated by the Gemini Flash extractor; consumed by the cross-checker
 * and by the prompt block.
 */
export interface ExtractedClaim {
  /** Stable id within a SiteContext — UUID. */
  id: string;
  /** Tag keyed against ClaimCategory. */
  category: ClaimCategory;
  /**
   * DR1 — sub-field name within the category. Optional because the
   * comparator-keyed categories are uniquely identified by their
   * category alone; the umbrella `'general'` category needs a field
   * tag to distinguish (e.g. `'brief'` for the migrated description).
   * Authors writing a 'general' claim must populate this.
   */
  field?: string;
  /** The 1-2 sentence excerpt of `siteContext.text` this claim came from. */
  sourceSpan: string;
  /**
   * Part C SP4 S5 — character span of `sourceSpan` within the source
   * document this claim was extracted from (when document-extracted).
   * Optional / additive: legacy claims and prose-authored claims have
   * none; consumers MUST tolerate `sourceOffset === undefined`. When
   * `source === 'document-extracted'`, `sourceDocId` identifies the doc
   * the offsets index into. Population is a follow-on step.
   */
  sourceOffset?: PassageOffset;
  /** Normalised claim — short bullet form the LLM cites (≤120 chars). */
  claim: string;
  /** When the claim asserts a value, what value did the extractor read? */
  claimedValue?: string | number | boolean;
  /** SED field path the claim maps to, or undefined when unverifiable. */
  sedField?: SedFieldPath;
  /** Extractor's confidence in the parse (0-100). */
  extractorConfidence: number;
  /**
   * DR1 — short note attributing the origin of the claim when it
   * wasn't produced by the standard SC3 extractor (e.g. lifted from
   * the legacy `site.description` field via the DR migrator). Mostly
   * for audit / display — comparators do not key on it.
   */
  versionNote?: string;
  /**
   * DR1 — actor metadata for the writer that produced this claim.
   * Mirrors `lastWrittenBy` shape but separate because the migrated
   * brief carries `system:legacy-description-migration` not a uid.
   */
  writtenBy?: string;
  /**
   * SA6 — SED paths this claim is bi-directionally bound to. Populated
   * by the SED_BINDING_REGISTRY lookup (not by the extractor). When set,
   * an edit to either the claim's value or the SED's value at any of
   * these paths propagates to the other side via the sync helpers.
   *
   * The registry is the source of truth — this field is denormalised
   * for cheap reads on the UI side. Authors edit the registry, not the
   * stored claim.
   */
  bindsToSedPaths?: readonly SedFieldPath[];
  /** ISO timestamp of the last sync write touching this claim. */
  lastWrittenAt?: string;
  /** UID / email of the actor whose write last touched this claim. */
  lastWrittenBy?: { uid: string; email?: string };
  /**
   * SA7 — append-only history of sync resolutions for this claim.
   * Each entry captures one user's "Adopt SED value" / "Adopt Site
   * Context value" decision in the diff tab, or an automatic sync.
   * Preserved across SED rebuild via UD's mergePreservedUserDecisions.
   */
  syncHistory?: readonly SyncHistoryEntry[];
  /**
   * PL3 — provenance discriminator. Tells the UI whether to render a
   * "from {fileName}" chip (`document-extracted`), a "merged from
   * multiple sources" badge (`merged`), or the default user-authored
   * chip (`user-typed`). Optional for back-compat: claims persisted
   * before PL3 shipped have no `source` and the UI treats absent
   * ⇒ `'user-typed'`.
   */
  source?: 'user-typed' | 'document-extracted' | 'merged';
  /**
   * PL3 — document id this claim was extracted from. Populated only
   * when `source === 'document-extracted'`. Lets the UI render
   * "extracted from {fileName}" by looking up the doc in site.documents.
   */
  sourceDocId?: string;
  /**
   * UAE-1 — Coverage Matrix requirement this claim was authored against
   * (e.g. `'geotech'`, `'utility-letter'`, `'zoning'`). Populated only
   * when the user clicked `[Author]` from the Coverage Matrix; standard
   * SC3-extracted claims and PL3 doc-extracted claims have no
   * `requirementId`. Used by:
   *   - `CoverageMatrix.resolveCell` to flip the row chip to the
   *     `'authored'` status.
   *   - `applyDocAwareConfidence` / SSM's evidence resolver to slot the
   *     claim's structured fields into the matching SED section as
   *     `'verified-from-context'` evidence.
   *   - `mergeClaimsIntoSiteContext` to avoid collapsing the authored
   *     claim into a same-text doc-extracted claim on the same
   *     requirement (the two represent distinct provenance — both stay
   *     in `claims[]`).
   */
  requirementId?: string;
  /**
   * UAE-1 — discriminator for how the user authored the claim:
   *   - `'structured'` ⇒ at least one field in `structuredFields` is
   *     populated; the dialog rendered the per-requirement form.
   *   - `'prose'`      ⇒ only the free-text `notes` field was filled;
   *     `structuredFields` may be empty.
   * Optional for back-compat — claims persisted before UAE-1 have no
   * `authoredKind` and consumers treat absent ⇒ "not user-authored
   * against a requirement" (i.e. legacy SC3 or PL3 claim).
   */
  authoredKind?: 'structured' | 'prose';
  /**
   * UAE-1 — per-field values from the requirement's authoredSchema
   * (`ChecklistItem.authoredSchema` in `documentChecklists.ts`). Keys
   * are `AuthoredFieldDef.key`; values are coerced to the declared
   * `AuthoredFieldType` at write time. Boolean/number/string union
   * keeps the persisted shape Firestore-friendly without inventing a
   * third claim type for the authored case.
   */
  structuredFields?: Record<string, string | number | boolean>;
  /**
   * UAE-1 — append-only archive of prior versions when the same
   * `(category, requirementId)` tuple is rewritten by a later authored
   * edit. Mirrors `syncHistory`'s shape (claim diff trail) but keyed at
   * the whole-claim level so the dialog can offer "Replace from prior
   * version" later (`UAE-FOLLOWUP-VERSIONING-UI`). Capped at the most
   * recent 10 versions by the writer.
   */
  priorVersions?: ExtractedClaim[];
  /**
   * SCE-2 / SCE-4 — quality-warning tags. Stamped at extract time when
   * the typed-field schema (`src/lib/sed/fieldSchema.ts`) flagged a
   * `severity: 'warn'` rule, OR at read time by the lazy validator
   * (SCE-4) when a legacy claim would have been rejected if extracted
   * today. UI renders an amber chip per tag — does NOT block the claim
   * from being used in the prompt block.
   *
   * Tag namespace: every tag starts with `sce-` so consumers can
   * filter the SCE family without hard-coding individual codes. See
   * `SceQualityWarning` in `fieldSchema.ts`.
   *
   * Absent ⇒ no warnings (the common case).
   */
  qualityWarnings?: readonly string[];
  /**
   * SCE-3 — user disposition for the claim. Drives downstream
   * reads + the diff-tab surface:
   *   - `undefined` (default) ⇒ claim is live, participates in
   *     cross-check + sync + prompt block.
   *   - `'mis-extracted'`     ⇒ user marked the claim as a typed
   *     extractor failure. The claim row STAYS in `claims[]` for
   *     audit, but every read-side surface (diff tab, prompt block,
   *     comparator) filters it out. Mirrors the "soft-delete" pattern
   *     elsewhere in the codebase — undo is one click via the
   *     SED Variable Browser's `[↺ Restore]` chip.
   *   - `'authored'` (future) ⇒ reserved for a future "user authored
   *     this claim directly" state distinct from the doc-extracted
   *     surface. Not used today.
   */
  state?: 'mis-extracted' | 'authored';
  /**
   * SCE-3 — telemetry on the mis-extraction. Mirrors `syncHistory` in
   * shape. Lets the admin "Extractor rejections" rollup attribute the
   * user-flagged mis-extraction to a specific user + time even though
   * the claim itself stays in `claims[]` for audit.
   */
  misExtractedAt?: string;
  misExtractedBy?: { uid: string; email?: string };
}

/**
 * SA7 — single resolution entry recorded on a claim's `syncHistory`.
 * The diff tab's "Adopt SED value" / "Adopt Site Context value" buttons
 * each append one entry. Auto-sync writes append `resolution: 'auto'`.
 */
export interface SyncHistoryEntry {
  /** The SED path this entry refers to (one claim can bind to many). */
  sedPath: SedFieldPath;
  /** Which side won. 'auto' marks a no-conflict / propagation write. */
  resolution: 'adopt-sed' | 'adopt-context' | 'auto';
  /** ISO timestamp of the resolution. */
  resolvedAt: string;
  /** UID / email of the resolver. */
  resolvedBy?: { uid: string; email?: string };
}

/**
 * A conflict raised by the deterministic cross-checker between an
 * extracted claim and the value the SED has for the same field.
 *
 * Severity tiers drive UI behaviour:
 *  - 'high'   — blocks submit until acknowledged.
 *  - 'medium' — surfaced as a warning chip.
 *  - 'low'    — informational chip; doesn't gate submit.
 */
export interface ClaimConflict {
  /** Stable id — generally `c-${claim.id}`. */
  id: string;
  /** The ExtractedClaim.id this conflict belongs to. */
  claimId: string;
  /** The SED field the comparator inspected. */
  sedField: SedFieldPath;
  /** The user's claim, normalised for display. */
  claimedValue: string;
  /** The SED's value, normalised for display. */
  sedValue: string;
  severity: 'high' | 'medium' | 'low';
  /** One-sentence explanation suitable for the inline warning chip. */
  explanation: string;
}

/**
 * The complete declared site-context payload. Lives on `SiteEntity.siteContext`
 * and is snapshotted onto `SiteRagReport.siteContextSnapshot` at generation
 * time.
 *
 * Reproducibility note: every field here is frozen onto the report at
 * `generateTypedReport`. Subsequent edits to the site's context do NOT
 * retroactively mutate prior reports — the snapshot is the source of
 * truth for report-level reads.
 */
export interface SiteContext {
  /** Raw user-authored prose. Markdown-friendly. Capped at 8K chars. */
  text: string;
  /** ISO timestamp of last user edit. */
  updatedAt: string;
  /** Author uid + email (for multi-user companies). */
  updatedBy?: { uid: string; email?: string };
  /** Structured claims extracted from `text` — populated by SC3 extractor. */
  claims: ExtractedClaim[];
  /** Cross-check results from SC4 — comparator output vs current SED. */
  conflicts: ClaimConflict[];
  /**
   * Conflicts the user has explicitly acknowledged. The LLM still sees
   * the conflict via the prompt block — acknowledgement only suppresses
   * the UI warning so the user can submit.
   */
  acknowledgedConflictIds: string[];
  /**
   * Version of the extractor model used. Lets us re-extract on model
   * upgrade without losing the prior parse for diffing. Format:
   * `gemini-flash-<YYYY.MM.DD>-v<N>` or `regex-backstop-v<N>` on the
   * fallback path.
   */
  extractorVersion: string;
  /**
   * CLP-1 — classified outcome of the last extractor invocation. Lets
   * the UI distinguish "never ran" (idle) from "ran-and-quota-429'd"
   * (quota-error) from "ran-and-genuinely-empty" (success with
   * `claims.length === 0`). Optional for back-compat: contexts persisted
   * before CLP shipped have no `extractorStatus` and the UI treats
   * absent ⇒ `'idle'`.
   *
   * Status values mirror `ExtractorStatus` in
   * `services/siteContext/extractClaims.ts` — kept as a string union
   * here (rather than importing the type) to avoid a runtime import
   * cycle between the type module and the extractor service.
   */
  extractorStatus?: 'idle' | 'extracting' | 'success' | 'auth-error' | 'quota-error' | 'timeout' | 'parse-error' | 'unknown-error';
  /**
   * CLP-1 — human-readable error string captured on the last failed
   * extraction. Surfaced in the chip's `title` tooltip so support /
   * power-users can copy the underlying error. Populated only when
   * `extractorStatus !== 'success' && extractorStatus !== 'idle'`.
   */
  extractorError?: string;
  /**
   * CLP-1 — ISO timestamp of the last extractor invocation. Distinct
   * from `updatedAt` (which moves on every text edit / claim mutation);
   * `extractedAt` only moves when the extractor actually ran.
   */
  extractedAt?: string;
  /**
   * DCX-1 — per-document context-scan receipts for the SERVER gap-fill
   * path (sed-bootstrap job). A doc attached OUTSIDE the client (script
   * attach, server-side write) never runs the PL3 upload-time extractor,
   * so the bootstrap job scans it and records the outcome here. The
   * marker is what makes a zero-claim scan idempotent: without it every
   * bootstrap would re-burn a Gemini call on a doc that legitimately
   * yields no claims. Only stamped on `status === 'success'` runs — a
   * quota/timeout failure leaves no marker so the next bootstrap retries.
   * Client PL3 does NOT stamp this (its trigger is the upload itself);
   * a client-uploaded zero-claim doc gets at most one server re-scan.
   */
  docContextScans?: Record<string, {
    /** ISO timestamp of the scan. */
    at: string;
    /** Extractor version that ran (mirrors `extractorVersion` format). */
    extractorVersion: string;
    /** Claims the scan contributed (post-merge `added` count). */
    claimCount: number;
    /** Full body, raw-file hash, extraction version and update timestamp consumed by the scan. */
    inputFingerprint?: string;
    /** Hash of the complete extracted source body (never the site-record preview). */
    bodyHash?: string;
    /** Unsupported model interpretations retained for review, never ordinary facts. */
    quarantinedClaims?: Array<{
      claim: ExtractedClaim;
      reason: 'date-without-operating-event' | 'fiber-title-or-legend-only';
      sourceContext: string;
      sourceContextOffset: { start: number; end: number };
      priorClaims?: ExtractedClaim[];
    }>;
  }>;
}

/**
 * Default empty SiteContext used as the starting point in the wizard
 * and when a legacy site has no `siteContext` field yet. Centralised
 * so callers don't drift on the shape.
 */
/** Coerce persisted / Firestore `siteContext.text` to a safe prose string. */
export function siteContextProseText(text: unknown): string {
  return typeof text === 'string' ? text.trim() : '';
}

export function hasSiteContextProse(context?: SiteContext | null): boolean {
  return siteContextProseText(context?.text).length > 0;
}

/**
 * Whether a context has any live content suitable for report generation.
 * Operator answers can create structured claims without adding prose, so a
 * prose-only gate would silently omit those answers from future reports.
 */
export function hasSiteContextContent(context?: SiteContext | null): boolean {
  if (hasSiteContextProse(context)) return true;
  return (context?.claims ?? []).some((claim) => (
    claim?.state !== 'mis-extracted'
    && !(claim?.versionNote === 'iia-3:image-observation' && claim.state !== 'authored')
    && typeof claim?.claim === 'string'
    && claim.claim.trim().length > 0
  ));
}

export function emptySiteContext(updatedAt?: string): SiteContext {
  return {
    text: '',
    updatedAt: updatedAt ?? new Date().toISOString(),
    claims: [],
    conflicts: [],
    acknowledgedConflictIds: [],
    extractorVersion: 'none',
    // CLP-1 — explicit `'idle'` so consumers can rely on the field
    // being present after construction.
    extractorStatus: 'idle',
  };
}

/**
 * Hard cap on raw input length. Anything beyond this is rejected by
 * `updateSiteContext` so a runaway paste can never bloat Firestore or
 * the LLM prompt.
 */
export const SITE_CONTEXT_MAX_CHARS = 8000;

/**
 * Soft warning threshold — UI flips the char counter colour. The hard
 * cap is `SITE_CONTEXT_MAX_CHARS`.
 */
export const SITE_CONTEXT_WARN_CHARS = 7500;
